import { AfterViewInit, Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ColumnApi, GridApi, GridOptions, GridReadyEvent } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { EditDeleteActionsRenderer } from "src/app/components/shared/ag-grid/cell-renderers/edit-delete.component";
import { Utilities } from "src/app/models/utilities";
import { AppCoreService } from "src/app/services/app-core.service";
import { CustomerService } from "src/app/services/customer.service";
import { GeoService } from "src/app/services/geo.service";
import { ProductService } from "src/app/services/products.service";
import { ChargesService } from "src/app/services/system-config/charges/charges.service";
import { VendorService } from "src/app/services/vendor.service";
import Swal from "sweetalert2";

@Component({
    selector: 'app-fuelsurcharges',
    templateUrl: './fuelsurcharges.component.html',
    styleUrls: ['./fuelsurcharges.component.scss']
})

export class FuelSurchargesComponent implements OnInit, AfterViewInit {
    fuelSurchargesForm: FormGroup;
    isFormSubmitted = false;
    isFuelSurchargesEdit = false;
    customerOptions: any[] = [];
    vendorOptions: any[] = [];
    serviceOptions: any[] = [];
    productOptions: any[] = [];
    destinationcountryList: any[] = [];
    destinationstateList: any[] = [];
    destinationcityList: any[] = [];
    currentDate: any;
    fuelsurchargesId: any;
    customerValue: any;
    chargeType: string;
    chargeTypeOptions: any[] = [
        { id: 1, name: 'Customer' },
        { id: 2, name: 'Vendor' },
    ]
    private gridApi!: GridApi;
    private gridColumnApi!: ColumnApi;
    public context: any;

    defaultColDef = {
        sortable: true,
        resizable: false,
        filter: true
    };

    gridOptions: GridOptions = {
        pagination: true,
        paginationPageSize: 10,
        domLayout: 'autoHeight'
    }

    columnDefs = [
        { headerName: 'Sr No', field: 'id', width: 120 },
        { headerName: 'Charge Type', field: 'chargetype' },
        { headerName: 'From Date', field: 'fromdate' },
        { headerName: 'To Date', field: 'todate' },
        { headerName: 'Percentage', field: 'percentage' },
        { headerName: 'Customer', field: 'customername' },
        { headerName: 'Vendor', field: 'vendorname' },
        { headerName: 'Service', field: 'servicename' },
        { headerName: 'Product', field: 'productname' },
        {
            headerName: 'Destination', marryChildren: true, children: [
                { headerName: ' Country', field: 'destinationcountryname' },
                { headerName: ' State', field: 'destinationstatename' },
                { headerName: ' City', field: 'destinationcityname' }
            ]
        },
        {
            headerName: 'Actions',
            field: 'id',
            cellRendererParams: { context: this },
            cellRenderer: EditDeleteActionsRenderer,
            colId: 'params',
            editable: false,
            sortable: false,
            minWidth: 80,
            filter: false
        },
    ];
    rowData = [];
    useEnterKey: any;
    onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;
        params.api.sizeColumnsToFit();
    }

    constructor(
        private formBuilder: FormBuilder,
        private toastr: ToastrService,
        private ngxLoaderService: NgxUiLoaderService,
        private customerService: CustomerService,
        private vendorService: VendorService,
        private productService: ProductService,
        private geoService: GeoService,
        private chargesService: ChargesService,
        private appCoreService: AppCoreService,
        private utilities: Utilities,
    ) { this.context = { componentParent: this }; }

    ngOnInit(): void {
        this.currentDate = this.formateDate().dateString;
        this.fuelSurchargesForm = this.formBuilder.group({
            ddlChargeType: [null, Validators.compose([Validators.required])],
            txtFromDate: [this.currentDate, Validators.compose([Validators.required])],
            txtToDate: [this.currentDate, Validators.compose([Validators.required])],
            txtPercentage: ['1.00', Validators.compose([Validators.required, Validators.pattern(/^([1-9]\d{0,1}(\.\d{1,2})?|100(\.00?)?)$/)])],
            txtCustomer: [null],
            txtVendor: [],
            txtService: [],
            txtProduct: [],
            destination: this.formBuilder.group({
                ddlCountry: [null],
                ddlState: [null],
                ddlCity: [null]
            }),
        });

        const userData = this.appCoreService.GetUserData();
        this.useEnterKey = !userData.metaInfo.tabbasedtemplate;
        this.bindCustomer();
        this.bindVendor();
        this.bindProduct();
        this.bindCountry();
    }

    ngAfterViewInit(): void {
        this.useEnterKey ? this.utilities.focusFirstElement(this.fuelSurchargesForm) : null;
    }

    bindCustomer() {
        this.customerService.GetAll('').subscribe((res) => {
            this.customerOptions = res?.success ? res.data : [];
        })
    }

    bindVendor() {
        this.vendorService.GetAll('').subscribe((res) => {
            this.vendorOptions = res?.success ? res.data : [];
        })
    }

    bindService() {
        const id = this.fuelSurchargesForm.get('txtVendor').value;
        this.fuelSurchargesForm.get('txtService').reset();
        this.vendorService.GetAllListByVendor({ id: id }).subscribe((res) => {
            this.serviceOptions = res?.success ? res.data : [];
        })
    }

    bindProduct() {
        this.productService.GetAll('').subscribe((res) => {
            this.productOptions = res?.success ? res.data : [];
        })
    }

    bindCountry() {
        this.geoService.GetAllCountries().subscribe((res) => {
            if (res && res.success) {
                this.destinationcountryList = res.data;
            }
        });
    }

    onDestinationCountryChange() {
        const destination: any = this.fuelSurchargesForm.get('destination');
        const selectedValue = destination.get('ddlCountry').value;
        if (selectedValue) {
            this.bindDestinationStatesByCountry(selectedValue);
            destination.get('ddlState').reset();
            destination.get('ddlCity').reset();
            this.destinationcityList = [];
        }
    }

    bindDestinationStatesByCountry(countryCode?: string) {
        if (countryCode) {
            this.geoService.GetAllStates({ countrycode: countryCode }).subscribe((res) => {
                this.destinationstateList = res?.success ? res.data : [];
            })
        }
    }

    onDestinationStateChange() {
        const destination: any = this.fuelSurchargesForm.get('destination');
        const selectedValue = destination.get('ddlState').value;
        if (selectedValue) {
            this.bindDestinationCitiesByState(selectedValue);
            destination.get('ddlCity').reset();
        }
    }

    bindDestinationCitiesByState(stateCode?: string) {
        try {
            if (stateCode) {
                this.geoService.GetAllCities({ statecode: stateCode }).subscribe((res) => {
                    this.destinationcityList = res?.success ? res.data : [];
                });
            }
        } catch (e) {
            console.error(e);
        }
    }

    selectChargeType() {
        this.isFormSubmitted = false;
        this.chargeType = this.fuelSurchargesForm.get('ddlChargeType').value;
        const customer = this.fuelSurchargesForm.get('txtCustomer').value;
        if (customer) {
            this.customerValue = customer;
        }
        if (this.chargeType == 'Vendor') {
            this.fuelSurchargesForm.get('txtCustomer').setValue(null);
            this.fuelSurchargesForm.get('txtCustomer').disable();
            this.fuelSurchargesForm.get('txtCustomer').clearValidators();
            this.fuelSurchargesForm.get('txtVendor').setValidators([Validators.required]);
            this.fuelSurchargesForm.get('txtCustomer').updateValueAndValidity();
            this.fuelSurchargesForm.get('txtVendor').updateValueAndValidity();
            this.rowData = [];
        }
        else {
            this.fuelSurchargesForm.get('txtCustomer').enable();
            this.fuelSurchargesForm.get('txtVendor').clearValidators();
            this.fuelSurchargesForm.get('txtCustomer').setValidators([Validators.required]);
            this.fuelSurchargesForm.get('txtVendor').updateValueAndValidity();
            this.fuelSurchargesForm.get('txtCustomer').updateValueAndValidity();
            this.rowData = [];
        }
        this.onValueChange(this.chargeType);
    }

    onValueChange(type?: any) {
        const chargetype = this.fuelSurchargesForm.get('ddlChargeType').value;
        this.chargeType = type;
        let id: number;
        if (type === 'Customer') {
            id = this.fuelSurchargesForm.get('txtCustomer').value;
            this.ngxLoaderService.start();
            this.chargesService.FuelSurcharges_GetAll({ type, id }).subscribe((res) => {
                this.rowData = res?.success ? res.data : [];
                this.ngxLoaderService.stop();
            });
            this.ngxLoaderService.stop();
        }
        else {
            if (chargetype === 'Vendor') {
                id = this.fuelSurchargesForm.get('txtVendor').value;
                this.fuelSurchargesForm.get('txtService').setValue(null);
                this.ngxLoaderService.start();
                this.chargesService.FuelSurcharges_GetAll({ type, id }).subscribe((res) => {
                    this.rowData = res?.success ? res.data : [];
                    this.ngxLoaderService.stop();
                });
                this.ngxLoaderService.stop();
            }
        }
    }

    getFuelSurcharges(data: any) {
        this.ngxLoaderService.start();
        this.isFuelSurchargesEdit = true;
        this.fuelsurchargesId = data.id;


        this.fuelSurchargesForm.patchValue({
            ddlChargeType: data.chargetype,
            txtFromDate: this.formateDate(data.fromdate).dateString,
            txtToDate: this.formateDate(data.todate).dateString,
            txtPercentage: data.percentage,
            txtCustomer: data.customerid,
            txtVendor: data.vendorid,
            txtProduct: data.productid,
            txtService: data.serviceid,
        });

        if (data.vendorid) {
            this.bindService();
            this.fuelSurchargesForm.get('txtService').setValue(data.serviceid);
        }

        this.bindDestinationStatesByCountry(data.destinationcountrycode);
        this.bindDestinationCitiesByState(data.destinationstatecode);
        this.fuelSurchargesForm.get('destination').patchValue({
            ddlCountry: data.destinationcountrycode,
            ddlState: data.destinationstatecode,
            ddlCity: data.destinationcitycode
        });

        this.ngxLoaderService.stop();
    }

    upsertFuelSurcharges() {
        this.isFormSubmitted = true;
        const destination = this.fuelSurchargesForm.get('destination');
        const fromdate = this.fuelSurchargesForm.get('txtFromDate').value;
        const todate = this.fuelSurchargesForm.get('txtToDate').value;
        if (fromdate > todate) {
            this.showErrorNotification("From Date can't be greater than To Date.");
            return;
        }
        if (this.fuelSurchargesForm.valid) {
            const reqObj = {
                id: this.fuelsurchargesId,
                chargetype: this.fuelSurchargesForm.get('ddlChargeType').value,
                fromdate: this.fuelSurchargesForm.get('txtFromDate').value,
                todate: this.fuelSurchargesForm.get('txtToDate').value,
                percentage: this.fuelSurchargesForm.get('txtPercentage').value,
                customerid: this.fuelSurchargesForm.get('txtCustomer').value,
                vendorid: this.fuelSurchargesForm.get('txtVendor').value,
                serviceid: this.fuelSurchargesForm.get('txtService').value,
                productid: this.fuelSurchargesForm.get('txtProduct').value,
                destinationcountrycode: destination.get('ddlCountry').value,
                destinationstatecode: destination.get('ddlState').value,
                destinationcitycode: destination.get('ddlCity').value
            }
            if (this.isFuelSurchargesEdit) {
                this.ngxLoaderService.start();
                this.chargesService.FuelSurcharges_Update(reqObj).subscribe((res) => {
                    if (res && res.success) {
                        this.ngxLoaderService.stop();
                        this.openSuccessMessageDialog('Record Update Successfully');
                        this.resetForm();
                    }
                    else if (res && !res.success) {
                        this.ngxLoaderService.stop();
                        this.openWarningMessageDialog(res.message);
                    }
                })
                this.ngxLoaderService.stop();
            }
            else {
                this.ngxLoaderService.start();
                this.chargesService.FuelSurcharges_Add(reqObj).subscribe((res) => {
                    if (res && res.success) {
                        this.ngxLoaderService.stop();
                        this.openSuccessMessageDialog('Record Added Successfully');
                        this.resetForm();
                    }
                    else if (res && !res.success) {
                        this.ngxLoaderService.stop();
                        this.openWarningMessageDialog(res.message);
                    }
                });
                this.ngxLoaderService.stop();
            }
        }
    }

    deleteRecord(id: any) {
        try {
            if (id) {
                this.ngxLoaderService.start();
                this.chargesService.FuelSurcharges_Delete(id).subscribe((res) => {
                    if (res && res.success) {
                        this.ngxLoaderService.stop();
                        this.onValueChange(this.chargeType);
                    }
                });
            }
        } catch (e) {
            this.ngxLoaderService.stop();
            console.error(e);
        }
    }

    resetForm() {
        this.chargeType = this.fuelSurchargesForm.get('ddlChargeType').value;
        const customer = this.fuelSurchargesForm.get('txtCustomer').value;
        const vendor = this.fuelSurchargesForm.get('txtVendor').value;
        this.fuelSurchargesForm.reset();
        this.isFormSubmitted = false;
        this.isFuelSurchargesEdit = false;
        this.fuelsurchargesId = null;
        this.fuelSurchargesForm.patchValue({
            ddlChargeType: this.chargeType,
            txtCustomer: customer,
            txtVendor: vendor,
            txtFromDate: this.currentDate,
            txtToDate: this.currentDate,
            txtPercentage: '1.00'
        });

        this.rowData = [];
    }

    editAction(cell: any) {
        try {
            if (cell && cell.id) {
                this.getFuelSurcharges(cell);
            }
        } catch (e) {
            console.error(e);
        }
    }

    deleteAction(cell: any) {
        if (cell && cell.id) {
            this.openConfirmationDialog('Are you sure you want to delete ?', cell);
        }
    }

    showWarningNotification(msg: any) {
        this.toastr.warning(msg, 'Warning');
    }

    showErrorNotification(msg: any) {
        this.toastr.error(msg, 'Error');
    }

    async openConfirmationDialog(msg: any, data?: any) {
        const result = await Swal.fire({
            titleText: 'Confirmation',
            text: msg,
            icon: 'warning',
            confirmButtonText: 'Yes',
            showDenyButton: true,
            denyButtonText: 'No',
            allowEscapeKey: false,
            allowOutsideClick: false
        });
        if (result.isConfirmed) {
            this.deleteRecord(data.id);
        }
    }

    async openSuccessMessageDialog(msg: any) {
        const result = await Swal.fire({
            title: 'Success!',
            text: msg,
            icon: 'success',
            confirmButtonText: 'OK',
            allowEscapeKey: false,
            allowOutsideClick: false
        })
        if (result.isConfirmed) {
            this.onValueChange(this.chargeType);
        }
    }

    openWarningMessageDialog(msg: any) {
        Swal.fire({
            title: 'Warning!',
            text: msg,
            icon: 'warning',
            confirmButtonText: 'OK',
            allowEscapeKey: false,
            allowOutsideClick: false
        })
    }

    formateDate(date?: any) {
        const inputDate = date != null || undefined ? new Date(date) : new Date();
        const year = inputDate.getFullYear();
        const month = (inputDate.getMonth() + 1).toString().padStart(2, '0');
        const day = inputDate.getDate().toString().padStart(2, '0');
        const hh = inputDate.getHours() < 10 ? ('0' + inputDate.getHours()) : inputDate.getHours();
        const mm = inputDate.getMinutes() < 10 ? ('0' + inputDate.getMinutes()) : inputDate.getMinutes();
        const ss = inputDate.getSeconds() < 10 ? ('0' + inputDate.getSeconds()) : inputDate.getSeconds();
        const dateTimeString = `${year}-${month}-${day}T${hh}:${mm}`;
        const dateString = `${year}-${month}-${day}`;
        return { dateString, dateTimeString };
    }
}